import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import useLeadSubmit, { Honeypot, LeadError } from '../useLeadSubmit';
import { sectionStyles, getCardClass, isCardTransparent, isColorDark, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const TYPE_COLORS = {
    'full-time': { bg: '#dcfce7', text: '#166534' },
    'part-time': { bg: '#dbeafe', text: '#1e40af' },
    'remote': { bg: '#f3e8ff', text: '#6b21a8' },
    'freelance': { bg: '#fef3c7', text: '#92400e' },
    'contract': { bg: '#fce7f3', text: '#9d174d' },
};

export default function JobApplicationSection({ block = {}, brand, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {}, currentPage }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'cards';
    const jobs = content.jobs || [];
    const formFields = content.form_fields || [];
    const successMessage = content.success_message || 'Tu aplicacion ha sido enviada exitosamente.';
    const emailTo = content.email_to || '';
    const ctaLink = content.cta_link || '';

    const [expandedJobIndex, setExpandedJobIndex] = useState(null);
    const [formData, setFormData] = useState({});
    const [submitted, setSubmitted] = useState({});

    if (jobs.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const toggleJob = (i) => {
        setExpandedJobIndex(expandedJobIndex === i ? null : i);
        setFormData({});
    };

    const handleFieldChange = (label, value) => {
        setFormData(prev => ({ ...prev, [label]: value }));
    };

    const leadSubmitHook = useLeadSubmit({
        brandId: brand?.id,
        blockType: 'job_application',
        blockId: block.id,
        pageId: currentPage?.id || null,
        source: 'Postulacion',
    });

    const handleSubmit = (e, job, jobIndex) => {
        e.preventDefault();
        const jobTitle = job.title || 'Puesto';

        // Save lead
        const leadData = { puesto: jobTitle };
        formFields.forEach(f => { if (formData[f.label]) leadData[f.label] = formData[f.label]; });
        leadSubmitHook.submit(leadData);

        // WhatsApp link
        if (ctaLink && (ctaLink.includes('wa.me') || ctaLink.includes('whatsapp'))) {
            let msg = `*Aplicacion: ${jobTitle}*\n`;
            formFields.forEach(f => {
                if (formData[f.label]) msg += `${f.label}: ${formData[f.label]}\n`;
            });
            const waUrl = ctaLink.includes('?') ? `${ctaLink}&text=${encodeURIComponent(msg)}` : `${ctaLink}?text=${encodeURIComponent(msg)}`;
            window.open(waUrl, '_blank');
            return;
        }

        // Email
        if (emailTo) {
            let body = `Aplicacion para: ${jobTitle}\n\n`;
            formFields.forEach(f => {
                if (formData[f.label]) body += `${f.label}: ${formData[f.label]}\n`;
            });
            window.location.href = `mailto:${emailTo}?subject=${encodeURIComponent(`Aplicacion: ${jobTitle}`)}&body=${encodeURIComponent(body)}`;
            return;
        }

        // Fallback: show success
        setSubmitted(prev => ({ ...prev, [jobIndex]: true }));
    };

    const getTypeColors = (type) => {
        const key = (type || '').toLowerCase().replace(/\s+/g, '-');
        return TYPE_COLORS[key] || { bg: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', text: s.textSub };
    };

    const requirements = (job) => (job.requirements || '').split('\n').filter(Boolean);

    const FormBlock = ({ job, jobIndex }) => {
        if (submitted[jobIndex]) {
            return (
                <div className="p-5 rounded-lg text-center" style={{ backgroundColor: hexToRgba(s.accent, 0.05) }}>
                    <svg className="w-10 h-10 mx-auto mb-2" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-medium" style={{ color: s.text }}>{successMessage}</p>
                </div>
            );
        }

        return (
            <form onSubmit={(e) => handleSubmit(e, job, jobIndex)} className="mt-5 pt-5 border-t space-y-4 relative" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb' }}>
                <Honeypot onFill={leadSubmitHook.setHoneypot} />
                <h5 className="text-sm font-bold" style={{ color: s.text }}>Aplicar a esta posicion</h5>
                {formFields.map((field, fi) => {
                    const fieldType = field.type || 'text';
                    const inputCls = "w-full px-4 py-2.5 rounded-lg border text-sm transition-all focus:outline-none focus:ring-2";
                    const inputStyle = { backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb', color: s.text };
                    return (
                        <div key={fi}>
                            <label className="block text-xs font-medium mb-1" style={{ color: s.textSub }}>{field.label}</label>
                            {fieldType === 'textarea' ? (
                                <textarea rows={3} value={formData[field.label] || ''} onChange={e => handleFieldChange(field.label, e.target.value)}
                                    className={inputCls} style={inputStyle} placeholder={field.label} />
                            ) : (
                                <input type={fieldType === 'phone' ? 'tel' : fieldType} value={formData[field.label] || ''} onChange={e => handleFieldChange(field.label, e.target.value)}
                                    className={inputCls} style={inputStyle} placeholder={field.label} />
                            )}
                        </div>
                    );
                })}
                <button type="submit" className="px-6 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90 hover:scale-105 shadow-lg"
                    style={{ backgroundColor: s.accent, color: isColorDark(s.accent) ? '#ffffff' : '#111827' }}>
                    Enviar aplicacion
                </button>
                <LeadError error={leadSubmitHook.error} />
            </form>
        );
    };

    const JobContent = ({ job, i, showForm }) => (
        <>
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h4 className="font-bold text-lg" style={{ color: s.text }}>{job.title}</h4>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        {job.department && (
                            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: hexToRgba(s.secondary, 0.15), color: s.secondary }}>
                                {job.department}
                            </span>
                        )}
                        {job.type && (
                            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: getTypeColors(job.type).bg, color: getTypeColors(job.type).text }}>
                                {job.type}
                            </span>
                        )}
                        {job.location && (
                            <span className="inline-flex items-center gap-1 text-xs" style={{ color: s.textSub }}>
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                {job.location}
                            </span>
                        )}
                    </div>
                </div>
                <button onClick={() => toggleJob(i)}
                    className="text-xs font-semibold px-4 py-2 rounded-lg transition-all hover:opacity-90 hover:scale-105"
                    style={expandedJobIndex === i
                        ? { backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.textSub }
                        : { backgroundColor: s.accent, color: isColorDark(s.accent) ? '#ffffff' : '#111827' }
                    }>
                    {expandedJobIndex === i ? 'Cerrar' : 'Aplicar'}
                </button>
            </div>

            {/* Description */}
            {job.description && <p className="text-sm mt-3 leading-relaxed" style={{ color: s.textSub }}>{job.description}</p>}

            {/* Requirements */}
            {expandedJobIndex === i && requirements(job).length > 0 && (
                <div className="mt-4">
                    <h5 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: s.text }}>Requisitos</h5>
                    <ul className="space-y-1.5">
                        {requirements(job).map((req, ri) => (
                            <li key={ri} className="flex items-start gap-2 text-sm" style={{ color: s.textSub }}>
                                <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                {req}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Form */}
            {showForm && expandedJobIndex === i && formFields.length > 0 && (
                <FormBlock job={job} jobIndex={i} />
            )}
        </>
    );

    // ── Accordion variant ──
    if (variant === 'accordion') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Trabaja con nosotros'} subtitle={content.subtitle} config={config} s={s} />
                    <div className="max-w-3xl mx-auto space-y-3">
                        {jobs.map((job, i) => (
                            <div key={i} className={`${card} overflow-hidden transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${150 + i * 100}ms` : '0ms' }}>
                                {/* Accordion header */}
                                <button onClick={() => toggleJob(i)}
                                    className="w-full flex items-center justify-between p-5 text-left">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h4 className="font-bold text-base" style={{ color: s.text }}>{job.title}</h4>
                                            {job.department && (
                                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(s.secondary, 0.15), color: s.secondary }}>
                                                    {job.department}
                                                </span>
                                            )}
                                            {job.type && (
                                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: getTypeColors(job.type).bg, color: getTypeColors(job.type).text }}>
                                                    {job.type}
                                                </span>
                                            )}
                                        </div>
                                        {job.location && (
                                            <span className="inline-flex items-center gap-1 text-xs mt-1" style={{ color: s.textSub }}>
                                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                                                {job.location}
                                            </span>
                                        )}
                                    </div>
                                    <svg className={`w-5 h-5 shrink-0 transition-transform duration-300 ${expandedJobIndex === i ? 'rotate-180' : ''}`}
                                        style={{ color: s.textSub }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {/* Expanded content */}
                                {expandedJobIndex === i && (
                                    <div className="px-5 pb-5">
                                        {job.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{job.description}</p>}
                                        {requirements(job).length > 0 && (
                                            <div className="mt-4">
                                                <h5 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: s.text }}>Requisitos</h5>
                                                <ul className="space-y-1.5">
                                                    {requirements(job).map((req, ri) => (
                                                        <li key={ri} className="flex items-start gap-2 text-sm" style={{ color: s.textSub }}>
                                                            <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                            {req}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {formFields.length > 0 && <FormBlock job={job} jobIndex={i} />}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── List variant ──
    if (variant === 'list') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Trabaja con nosotros'} subtitle={content.subtitle} config={config} s={s} />
                    <div className="max-w-3xl mx-auto">
                        {jobs.map((job, i) => (
                            <div key={i} className={`py-6 transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'} ${i > 0 ? 'border-t' : ''}`}
                                style={{ transitionDelay: visible ? `${150 + i * 100}ms` : '0ms', borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb' }}>
                                <JobContent job={job} i={i} showForm />
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Cards variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Trabaja con nosotros'} subtitle={content.subtitle} config={config} s={s} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                    {jobs.map((job, i) => (
                        <div key={i} className={`${card} p-6 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: visible ? `${150 + i * 100}ms` : '0ms' }}>
                            <JobContent job={job} i={i} showForm />
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
